/**
 * Entités juridiques (ELOCE, Edumiam…) publiées par walter-crm, seule source de la donnée juridique.
 *
 * Le CRM republie le JSON à chaque modification d'un Business Group. Le snapshot à côté de ce
 * fichier sert tant que le fichier publié n'est pas lu, et en local : le rafraîchir quand la donnée
 * juridique change.
 */
import snapshot from './legalEntities.snapshot.json'

export type LegalEntity = {
  slug: string
  legal_name: string
  /** Raison sociale suivie de la forme : « ELOCE SASU ». */
  designation: string
  activity_declaration: { number: string; number_display: string } | null
  qualiopi: { certificate_number: string } | null
  contacts: { email: string | null }
}

export type LegalEntitiesDocument = {
  entities: Record<string, LegalEntity>
  brands: Record<string, string>
  aliases: Record<string, string>
}

export const LEGAL_ENTITIES_SNAPSHOT = snapshot as unknown as LegalEntitiesDocument
const DEFAULT_ENTITY = 'walter-learning'
const PREFIXES: Record<string, string> = { PRODUCTION: 'prd', STAGING: 'stg' }

/** URL du JSON publié pour `VITE_ENV` ; null en local. */
export function legalEntitiesUrl(env?: string): string | null {
  const prefix = PREFIXES[env ?? '']
  return prefix ? `https://walter-crm.s3.amazonaws.com/${prefix}/public/legal-entities/v1.json` : null
}

/** Le JSON publié, ou le snapshot quand il n'est pas lisible. */
export async function fetchLegalEntities(env?: string): Promise<LegalEntitiesDocument> {
  const url = legalEntitiesUrl(env)
  if (!url) return LEGAL_ENTITIES_SNAPSHOT
  try {
    const response = await fetch(url)
    if (!response.ok) return LEGAL_ENTITIES_SNAPSHOT
    const document = (await response.json()) as LegalEntitiesDocument
    return isUsable(document) ? document : LEGAL_ENTITIES_SNAPSHOT
  } catch {
    return LEGAL_ENTITIES_SNAPSHOT
  }
}

/** Un document publié sans ELOCE, sans marques ou sans alias ne remplace pas la copie embarquée. */
function isUsable(document: LegalEntitiesDocument | null | undefined): document is LegalEntitiesDocument {
  return Boolean(document?.entities?.[DEFAULT_ENTITY]?.designation && document.brands && document.aliases)
}

function lookup(document: LegalEntitiesDocument, key: string): LegalEntity | undefined {
  const slug = document.brands?.[key] ?? document.aliases?.[key] ?? key
  const entity = document.entities?.[slug]
  return entity?.designation ? entity : undefined
}

/**
 * L'entité d'une marque (`edumiam`, `walter_sante`…) ou d'un alias ; ELOCE par défaut. Une entité absente du
 * document publié est prise dans la copie embarquée avant de retomber sur ELOCE : le tunnel ne casse jamais, et
 * Edumiam n'est jamais présentée comme une marque d'ELOCE faute de son entrée.
 */
export function resolveLegalEntity(document: LegalEntitiesDocument, brand?: string | null): LegalEntity {
  const key = (brand ?? '').toLowerCase()
  return (
    lookup(document, key) ??
    lookup(LEGAL_ENTITIES_SNAPSHOT, key) ??
    lookup(document, DEFAULT_ENTITY) ??
    LEGAL_ENTITIES_SNAPSHOT.entities[DEFAULT_ENTITY]
  )
}

/** « Walter Santé est une marque d'ELOCE SASU • NDA … • Processus certifié Qualiopi » ou « Edumiam SAS • NDA … ». */
export function legalMention(entity: LegalEntity, brandName: string): string {
  const holder = entity.designation.toLowerCase().startsWith(brandName.toLowerCase())
    ? entity.designation
    : `${brandName} est une marque ${/^[aeiouyéèh]/i.test(entity.designation) ? "d'" : 'de '}${entity.designation}`
  const parts = [holder]
  if (entity.activity_declaration) parts.push(`NDA ${entity.activity_declaration.number_display}`)
  if (entity.qualiopi) parts.push('Processus certifié Qualiopi')
  return parts.join(' • ')
}
